import { useMemo } from "react";
import { Popover } from "antd";
import { ArrowLeftOutlined, CloseOutlined, InfoCircleFilled } from "@ant-design/icons";
import { ProfessionalReadinessDetails } from "src/components/Common/ProfessionalReadinessPill";
import WithToolTip from "src/components/Common/WithToolTip";
import { getDefaultFormattedDate, roundOff } from "src/utils/helperFunctions";
import { getTraitBand } from "../../oprations";
import { span } from "./rules/format";

/*
 * The room's bar, in one row as 4.0 has it: the trust score ring, the verdict
 * and what it rests on, and the session's figures. The ring is the report's
 * own trust score (the image proctoring analysis's figure, with its remark and
 * when it came in behind a tooltip); the room computes no score of its own.
 * Professional readiness is a figure here too, and the notices (video turned
 * off by an admin, a camera that did not work, recordings out of reach, an
 * approximate clock) sit behind one ⓘ, so the recording keeps the height a
 * second row took. Narrower than 1100px the figures hide; readiness then moves
 * behind the ⓘ.
 */
export default function ReviewHeader({
  person, position, headline, facts, trustScore, positionConfig, linkConfig,
  cutOffCleared, cameraSupported, approxClock, unreachable, isCandidateReport, onClose,
}) {
  /* the trust score as a whole number in [0, 100], and its colour; null when the report holds none */
  const [trustColor, trustVal] = useMemo(() => {
    const n = Number(trustScore?.trustScore?.toString()?.split("%")?.[0]);
    if (!trustScore?.remark || !Number.isFinite(n)) return ["#B6B6B6", null];
    const pct = Math.max(0, Math.min(100, roundOff(n)));
    return [getTraitBand({ median: 50, lb: 25, ub: 75, score: pct }).textColor, pct];
  }, [trustScore]);
  const hideTrustDescription = positionConfig?.skipAiProcOnCutoffFail && cutOffCleared === false;
  const receivedAt = trustScore?.receivedAt;
  const videoDisabledByAdmin = linkConfig?.proctoring === false && linkConfig?.saveProctoredVideo === false && positionConfig?.saveProctoredVideo;
  const readiness = positionConfig?.trustScorePersonalityAnalysis ? trustScore?.professionalReadiness : null;
  const readinessScore = readiness ? Number(readiness.overallScore ?? 0) : null;
  const readinessColor = readiness ? getTraitBand({ median: 50, lb: 25, ub: 75, score: readinessScore * 10 }).textColor : undefined;
  const hasTrust = trustVal != null;

  const trustTip = hasTrust ? (
    <div>
      {!hideTrustDescription && <p className="text-white text-12 mb-4">{trustScore.remark}</p>}
      {receivedAt && <p className="text-white text-12">{`Received ${getDefaultFormattedDate(receivedAt, "Do MMM YYYY, hh:mm A")}`}</p>}
    </div>
  ) : null;

  const notices = [
    videoDisabledByAdmin && { key: "admin", bad: true, text: <><strong>Video proctoring</strong> was turned off by an admin for this candidate</> },
    cameraSupported === false && { key: "camera", bad: true, text: "The candidate's camera was not working" },
    unreachable && { key: "unreachable", bad: true, text: "Some recordings could not be reached when this review was built; what they hold is not shown" },
    approxClock && { key: "clock", bad: false, text: "Times from the candidate's computer are approximate: its clock could not be matched to the server's" },
  ].filter(Boolean);
  const anyBad = notices.some((n) => n.bad);
  const narrowFacts = !!readiness;

  const noticeList = (
    <div className="proctoring-review__notices">
      {narrowFacts && (
        <dl className="proctoring-review__notices-facts">
          {readiness && (
            <div>
              <dt>Professional readiness</dt>
              <dd style={{ color: readinessColor }}>{roundOff(readinessScore, 1)}</dd>
            </div>
          )}
        </dl>
      )}
      {notices.length > 0 && (
        <ul>
          {notices.map((n) => (
            <li key={n.key} className={`proctoring-review__notice${n.bad ? " proctoring-review__notice--bad" : ""}`}>
              <InfoCircleFilled /> <span>{n.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <header className="proctoring-review__bar">
      <button type="button" className="proctoring-review__bar-btn" onClick={onClose} aria-label="Back to the report" title="Back to the report">
        <ArrowLeftOutlined />
      </button>
      <WithToolTip title={trustTip} showToolTip={hasTrust && (!hideTrustDescription || !!receivedAt)}>
        <div
          className={`proctoring-review__ring${hasTrust ? "" : " proctoring-review__ring--none"}`}
          style={{ background: `conic-gradient(${trustColor} ${hasTrust ? trustVal : 0}%, #ECEDF1 0)` }}
          data-trust={hasTrust ? trustVal : ""}
          title={hasTrust ? undefined : "No trust score for this round"}
          aria-label={hasTrust ? `Trust score ${trustVal}%` : "No trust score for this round"}
        >
          {hasTrust ? <b>{trustVal}<small>%</small></b> : <b>—</b>}
        </div>
      </WithToolTip>
      <div className="proctoring-review__verdict">
        <span className="proctoring-review__who">{[person, position ? `${position} proctoring` : "Proctoring"].filter(Boolean).join(" · ")}</span>
        <b className="proctoring-review__verdict-word">{headline.verdict}</b>
        <p title={headline.line}>{headline.line}</p>
      </div>
      <dl className="proctoring-review__facts">
        <div><dt>Session</dt><dd>{facts.length ? span(facts.length) : "—"}</dd></div>
        <div title="Of the captures the image analysis checked, those with the candidate's face in view">
          <dt>Face matched</dt>
          <dd>{facts.faces ? `${facts.faces.matched} of ${facts.faces.total}` : "—"}</dd>
        </div>
        {!isCandidateReport && (
          <div>
            <dt>Tab switches</dt>
            {facts.tabs.count == null
              ? <dd title="The tab switch count was not recorded for this round">—</dd>
              : <dd>{facts.tabs.count}<small>{` of ${facts.tabs.limit} allowed`}</small></dd>}
          </div>
        )}
        <div title={facts.sessions > 1 ? "The candidate entered the assessment more than once; each entry is a session" : undefined}>
          <dt>Recording</dt>
          <dd>{facts.recording}{facts.sessions > 1 && <small>{` · ${facts.sessions} sessions`}</small>}</dd>
        </div>
        {readiness && (
          <WithToolTip title={<ProfessionalReadinessDetails professionalReadiness={readiness} />} showToolTip>
            <div className="proctoring-review__fact--tip">
              <dt>Readiness</dt>
              <dd style={{ color: readinessColor }}>{roundOff(readinessScore, 1)}</dd>
            </div>
          </WithToolTip>
        )}
      </dl>
      {(notices.length > 0 || narrowFacts) && (
        <Popover content={noticeList} trigger="click" placement="bottomRight" overlayClassName="proctoring-review__notices-pop">
          <button
            type="button"
            className={[
              "proctoring-review__bar-btn proctoring-review__notices-btn",
              anyBad ? "proctoring-review__notices-btn--bad" : "",
              notices.length ? "" : "proctoring-review__notices-btn--narrow",
            ].filter(Boolean).join(" ")}
            aria-label={notices.length ? `${notices.length} ${notices.length === 1 ? "notice" : "notices"} about this review` : "Professional readiness"}
            title={notices.length ? "Notices about this review" : "Professional readiness"}
          >
            <InfoCircleFilled />
            {notices.length > 0 && <span className="proctoring-review__notices-count">{notices.length}</span>}
          </button>
        </Popover>
      )}
      <button type="button" className="proctoring-review__bar-btn" onClick={onClose} aria-label="Close proctoring" title="Close (Esc)">
        <CloseOutlined />
      </button>
    </header>
  );
}
